/**
 * One proposal: what it quotes, where it stands, and — right after a send —
 * the acceptance link (FR-H4). A kit `Dialog`; a proposal has no page of its
 * own and no address for one. *Send* hands back to the page, which confirms.
 */

import { Button, Dialog, Input, formatDate } from '@fiveox/ui'
import { Copy, Send } from 'lucide-react'
import { useState } from 'react'

import { StatusBadge } from '../../../components/page'
import { type ProposalRecord, canSend, money, proposalSubtitle } from '../../../lib/api/proposals'

/** What a send said, kept by the page so the dialog can show it. */
export interface SentLink {
  note: string
  link: string | null
}

export function ProposalDialog({
  row,
  leadName,
  mayUpdate,
  sent,
  onClose,
  onSend,
}: {
  row: ProposalRecord
  leadName: string | null
  mayUpdate: boolean
  sent: SentLink | null
  onClose: () => void
  onSend: () => void
}) {
  return (
    <Dialog open onClose={onClose} title={row.title ?? 'Proposal'} width="lg">
      <div className="space-y-5">
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge kind="proposal" status={row.status} />
            {leadName && <span className="text-foreground text-sm">To {leadName}</span>}
          </div>
          <p className="text-muted-foreground text-sm">
            {proposalSubtitle(row) || 'A draft — it gets its number when it is sent.'}
          </p>
          {row.sent_at && (
            <p className="text-muted-foreground text-xs">Sent {formatDate(row.sent_at)}</p>
          )}
          {row.status === 'declined' && row.decline_reason && (
            <p className="text-foreground text-sm">Declined: {row.decline_reason}</p>
          )}
        </div>

        {sent && <LinkBox sent={sent} />}

        <section className="border-border border-t pt-4">
          <h3 className="text-foreground mb-3 text-sm font-semibold">What it quotes</h3>
          <ul className="divide-border divide-y text-sm">
            {row.lines.map((line) => (
              <li key={line.line_no} className="flex justify-between gap-4 py-2">
                <span className="text-foreground min-w-0">{line.description}</span>
                <span className="text-foreground shrink-0 tabular-nums">
                  {money(line.amount, row.currency)}
                </span>
              </li>
            ))}
            <li className="flex justify-between gap-4 py-2 font-semibold">
              <span className="text-foreground">Total</span>
              <span className="text-foreground tabular-nums">
                {money(row.total_amount, row.currency)}
              </span>
            </li>
          </ul>
        </section>

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          {mayUpdate && canSend(row) && (
            <Button onClick={onSend}>
              <Send size={16} aria-hidden /> {row.status === 'draft' ? 'Send it' : 'Send again'}
            </Button>
          )}
        </div>
      </div>
    </Dialog>
  )
}

/**
 * The link, after a send.
 *
 * A read-only field rather than a paragraph, because the thing a firm needs to
 * do with it is select all and copy, and a `<p>` makes that a drag.
 * `navigator.clipboard` is used when the browser offers it and the field is the
 * fallback — never the only route, because the clipboard API is unavailable on
 * an insecure origin and a copy button that silently does nothing is worse than
 * no button.
 */
function LinkBox({ sent }: { sent: SentLink }) {
  const [copied, setCopied] = useState(false)
  const { note, link } = sent
  if (!link) return <p className="text-muted-foreground text-sm">{note}</p>
  return (
    <div className="border-border bg-muted/40 space-y-2 rounded-xl border p-3" data-link-box="">
      <p className="text-foreground text-sm">{note}</p>
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1">
          <Input
            size="sm"
            readOnly
            value={link}
            onFocus={(event) => event.target.select()}
            aria-label="Acceptance link"
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            void navigator.clipboard?.writeText(link).then(() => setCopied(true))
          }}
        >
          <Copy size={14} aria-hidden /> {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
    </div>
  )
}
